import { z } from "zod";

export const CONTRACT_VERSION = "canvas.stroke.v1" as const;
export const ROOM_ID = "anonymous-main" as const;
export const STROKE_EVENT = "stroke.snapshot.v1" as const;
export const CURSOR_EVENT = "cursor.position.v1" as const;
export const REVERB_CHANNEL = "canvas.public.v1" as const;
export const STROKE_TTL_MS = 30_000;
export const MAX_POINTS = 192;
export const MAX_EVENT_BYTES = 9_000;
export const BRUSH_IDS = ["round", "marker", "calligraphy", "spray", "pixel"] as const;
export const STICKER_IDS = [
  "laravel",
  "facade",
  "lambo",
  "enter",
  "cosmic-salute",
  "we-must-ship",
  "coke-zero",
  "ship-keys",
  "taylor",
] as const;
export const STORAGE_SNAPSHOTS_KEY = "canvas:public:v1:snapshots" as const;
export const STORAGE_EXPIRIES_KEY = "canvas:public:v1:expiries" as const;
export const STORAGE_EXPIRY_SCORE_UNIT = "unix-epoch-milliseconds" as const;

export const STORAGE_V1 = {
  snapshotsHash: STORAGE_SNAPSHOTS_KEY,
  expiriesZSet: STORAGE_EXPIRIES_KEY,
  expiryScoreUnit: STORAGE_EXPIRY_SCORE_UNIT,
} as const;

export const API_PATHS = {
  recentStrokes: "/api/v1/strokes/recent",
  ingestStroke: "/api/v1/strokes",
  updateCursor: "/api/v1/cursors",
  meta: "/api/v1/meta",
} as const;

const isoDate = z.iso.datetime({ offset: true });
const uuid = z.uuid();

export const pointSchema = z
  .tuple([
    z.number().finite().min(0).max(2_048),
    z.number().finite().min(0).max(2_048),
    z.number().finite().min(0).max(1),
  ])
  .readonly();

export const strokeStyleSchema = z
  .object({
    color: z.string().regex(/^#[0-9a-f]{6}$/i),
    size: z.number().finite().min(1).max(256),
    brush: z.enum(BRUSH_IDS).optional(),
    sticker: z.enum(STICKER_IDS).optional(),
    tool: z.enum(["eraser", "clear"]).optional(),
  })
  .strict()
  .superRefine((style, context) => {
    if (style.tool !== "eraser" && style.size > 32) {
      context.addIssue({
        code: "custom",
        path: ["size"],
        message: "Ink and sticker size cannot exceed 32",
      });
    }
    if (style.sticker && style.tool) {
      context.addIssue({
        code: "custom",
        path: ["tool"],
        message: "A sticker cannot also be an admin tool",
      });
    }
  });

export const clientStrokeSnapshotSchema = z
  .object({
    kind: z.literal("stroke.snapshot.client"),
    contractVersion: z.literal(CONTRACT_VERSION),
    roomId: z.literal(ROOM_ID),
    strokeId: uuid,
    clientId: uuid,
    revision: z.number().int().min(0).max(1_000_000),
    completed: z.boolean(),
    clientUpdatedAt: isoDate,
    style: strokeStyleSchema,
    points: z.array(pointSchema).min(1).max(MAX_POINTS),
  })
  .strict();

export const snapshotOriginSchema = z
  .object({
    adapter: z.enum(["express", "laravel", "flask", "go", "rails"]),
    framework: z.string().min(1).max(80),
    runtime: z.string().min(1).max(80),
    instanceId: z.string().min(1).max(120),
  })
  .strict();

export const serverStrokeSnapshotSchema = clientStrokeSnapshotSchema
  .omit({ kind: true })
  .extend({
    kind: z.literal("stroke.snapshot.server"),
    origin: snapshotOriginSchema,
    createdAt: isoDate,
    updatedAt: isoDate,
    expiresAt: isoDate,
  })
  .strict();

export const cursorPositionSchema = z
  .object({
    x: z.number().finite().min(0).max(2_048),
    y: z.number().finite().min(0).max(2_048),
  })
  .strict();

export const clientCursorPositionSchema = z
  .object({
    kind: z.literal("cursor.position.client"),
    contractVersion: z.literal(CONTRACT_VERSION),
    roomId: z.literal(ROOM_ID),
    clientId: uuid,
    sequence: z.number().int().min(0).max(1_000_000_000),
    position: cursorPositionSchema.nullable(),
    clientUpdatedAt: isoDate,
  })
  .strict();

export const serverCursorPositionSchema = clientCursorPositionSchema
  .omit({ kind: true })
  .extend({
    kind: z.literal("cursor.position.server"),
    origin: snapshotOriginSchema,
    updatedAt: isoDate,
  })
  .strict();

export const ingestResponseSchema = z
  .object({
    accepted: z.boolean(),
    snapshot: serverStrokeSnapshotSchema,
    storeLatencyMs: z.number().nonnegative(),
    broadcast: z
      .object({
        status: z.enum(["disabled", "ok", "error"]),
        latencyMs: z.number().nonnegative(),
      })
      .strict(),
  })
  .strict();

export const cursorUpdateResponseSchema = z
  .object({
    accepted: z.literal(true),
    cursor: serverCursorPositionSchema,
    broadcast: z
      .object({
        status: z.enum(["disabled", "ok", "error"]),
        latencyMs: z.number().nonnegative(),
      })
      .strict(),
  })
  .strict();

export const serviceStatusSchema = z.enum([
  "disabled",
  "ok",
  "degraded",
  "error",
]);

export const serviceTelemetrySchema = z
  .object({
    driver: z.string().min(1).max(80),
    status: serviceStatusSchema,
    latencyMs: z.number().finite().nonnegative(),
    lastCheckedAt: isoDate,
    detail: z.string().max(240).nullable(),
  })
  .strict();

const reverbConnectionFields = {
  channel: z.literal(REVERB_CHANNEL),
  event: z.literal(STROKE_EVENT),
};

export const publicReverbConfigurationSchema = z.discriminatedUnion("enabled", [
  z
    .object({
      enabled: z.literal(false),
      appKey: z.null(),
      host: z.null(),
      wsPort: z.null(),
      wssPort: z.null(),
      forceTLS: z.literal(false),
      cluster: z.null(),
      ...reverbConnectionFields,
    })
    .strict(),
  z
    .object({
      enabled: z.literal(true),
      appKey: z.string().min(1).max(200),
      host: z.string().min(1).max(253),
      wsPort: z.number().int().min(1).max(65_535),
      wssPort: z.number().int().min(1).max(65_535),
      forceTLS: z.boolean(),
      cluster: z.string().min(1).max(80).nullable(),
      ...reverbConnectionFields,
    })
    .strict(),
]);

export const recentStrokesResponseSchema = z
  .object({
    kind: z.literal("stroke.recent-list"),
    contractVersion: z.literal(CONTRACT_VERSION),
    roomId: z.literal(ROOM_ID),
    serverTime: isoDate,
    storeLatencyMs: z.number().finite().nonnegative(),
    snapshots: z.array(serverStrokeSnapshotSchema).max(500),
  })
  .strict();

export const metaTelemetryResponseSchema = z
  .object({
    kind: z.literal("canvas.meta"),
    contractVersion: z.literal(CONTRACT_VERSION),
    roomId: z.literal(ROOM_ID),
    serverTime: isoDate,
    adapter: snapshotOriginSchema,
    ttlMs: z.literal(STROKE_TTL_MS),
    limits: z
      .object({
        maxPoints: z.literal(MAX_POINTS),
        maxEventBytes: z.literal(MAX_EVENT_BYTES),
      })
      .strict(),
    reverb: publicReverbConfigurationSchema,
    telemetry: z
      .object({
        uptimeSeconds: z.number().finite().nonnegative(),
        store: serviceTelemetrySchema,
        reverb: serviceTelemetrySchema.extend({
          lastEventAt: isoDate.nullable(),
        }),
      })
      .strict(),
  })
  .strict();

export type Point = z.infer<typeof pointSchema>;
export type StrokeStyle = z.infer<typeof strokeStyleSchema>;
export type BrushId = (typeof BRUSH_IDS)[number];
export type StickerId = (typeof STICKER_IDS)[number];
export type ClientStrokeSnapshot = z.infer<typeof clientStrokeSnapshotSchema>;
export type SnapshotOrigin = z.infer<typeof snapshotOriginSchema>;
export type ServerStrokeSnapshot = z.infer<typeof serverStrokeSnapshotSchema>;
export type CursorPosition = z.infer<typeof cursorPositionSchema>;
export type ClientCursorPosition = z.infer<typeof clientCursorPositionSchema>;
export type ServerCursorPosition = z.infer<typeof serverCursorPositionSchema>;
export type CursorUpdateResponse = z.infer<typeof cursorUpdateResponseSchema>;
export type IngestResponse = z.infer<typeof ingestResponseSchema>;
export type ServiceStatus = z.infer<typeof serviceStatusSchema>;
export type ServiceTelemetry = z.infer<typeof serviceTelemetrySchema>;
export type PublicReverbConfiguration = z.infer<
  typeof publicReverbConfigurationSchema
>;
export type RecentStrokesResponse = z.infer<typeof recentStrokesResponseSchema>;
export type MetaTelemetryResponse = z.infer<typeof metaTelemetryResponseSchema>;

export function serializedByteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}
